import { Fragment } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { TrendUp } from "@phosphor-icons/react";

import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

import { message, formatDate, formatSnakeCase } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import type { SentimentByChannelData } from "@/schema/brand-os/ai-consumer-research";

const channelColorMap: Record<string, string> = {
  instagram: "var(--instagram)",
  tiktok: "var(--tiktok)",
  youtube: "var(--youtube)",
  x: "var(--x)",
  amazon: "var(--amazon)",
  reddit: "var(--reddit)",
  facebook: "var(--facebook)",
  flipkart: "var(--flipkart)",
};

type ChannelTrend = SentimentByChannelData["channels"][number];
type ChartRow = Record<string, string | number>;

function totalMentions(channel: ChannelTrend): number {
  return channel.trend.reduce((sum, p) => sum + p.positive_count + p.negative_count + p.neutral_count, 0);
}

function buildChart(data: SentimentByChannelData): { chartData: ChartRow[]; chartConfig: ChartConfig } {
  const monthly = data.channels.filter((c) => c.trend.some((p) => p.month !== "overall"));
  const overallOnly = data.channels.filter((c) => c.trend.every((p) => p.month === "overall"));

  const channels = [...[...monthly].sort((a, b) => totalMentions(b) - totalMentions(a)), ...[...overallOnly].sort((a, b) => totalMentions(b) - totalMentions(a))];

  const monthKeys = Array.from(new Set(monthly.flatMap((c) => c.trend.filter((p) => p.month !== "overall").map((p) => p.month)))).sort();

  const rows = new Map<string, ChartRow>(monthKeys.map((m) => [m, { month: formatDate(`${m}-01`, "MMM") }]));

  for (const channel of channels) {
    const key = channel.channel.toLowerCase().replace(/[^a-z0-9]/g, "_");
    const isOverallOnly = channel.trend.every((p) => p.month === "overall");

    if (isOverallOnly) {
      const score = channel.trend[0]?.sentiment_score ?? 0;
      for (const m of monthKeys) rows.get(m)![key] = score;
    } else {
      for (const p of channel.trend) {
        if (p.month !== "overall") rows.get(p.month)![key] = p.sentiment_score;
      }
    }
  }

  const chartConfig: ChartConfig = Object.fromEntries(
    channels.map((c) => {
      const key = c.channel.toLowerCase().replace(/[^a-z0-9]/g, "_");
      const colorKey = c.channel.toLowerCase().replace(/[^a-z]/g, "");
      return [
        key,
        {
          label: formatSnakeCase(key),
          color: channelColorMap[colorKey] ?? `hsl(${(colorKey.charCodeAt(0) * 37) % 360} 60% 50%)`,
        },
      ];
    })
  );

  return { chartData: monthKeys.map((m) => rows.get(m)!), chartConfig };
}

function SentimentByChannelComponent({ status, chartData, chartConfig }: { status: QueryStatus; chartData: ChartRow[]; chartConfig: ChartConfig }) {
  switch (status) {
    case "pending":
      return <SentimentByChannelSkeleton />;
    case "error":
      return <LockedContent message={message} className="min-h-64" />;
    case "success":
      return chartData.length ? <SentimentByChannelChart chartData={chartData} chartConfig={chartConfig} /> : <LockedContent message={message} className="min-h-64" />;
  }
}

export function SentimentByChannel() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.SentimentByChannel());

  const { chartData, chartConfig } = data ? buildChart(data) : { chartData: [], chartConfig: {} as ChartConfig };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="flex size-9 items-center justify-center rounded-lg bg-success/10 text-success">
            <TrendUp className="size-5" weight="bold" />
          </span>
          Sentiment by Channel
        </CardTitle>
        <CardDescription className="pl-11">Sentiment score per platform</CardDescription>
        <CardAction>
          <span className="text-xs text-muted-foreground">8-month rolling</span>
        </CardAction>
      </CardHeader>
      <CardContent>
        <SentimentByChannelComponent status={status} chartData={chartData} chartConfig={chartConfig} />
      </CardContent>
    </Card>
  );
}

function SentimentByChannelChart({ chartData, chartConfig }: { chartData: ChartRow[]; chartConfig: ChartConfig }) {
  const seriesKeys = Object.keys(chartConfig);
  const scores = chartData.flatMap((row) => seriesKeys.map((k) => row[k]).filter((v): v is number => typeof v === "number"));
  const minScore = scores.length ? Math.max(0, Math.floor((Math.min(...scores) - 5) / 10) * 10) : 0;
  const maxScore = scores.length ? Math.min(100, Math.ceil((Math.max(...scores) + 5) / 10) * 10) : 100;

  return (
    <Fragment>
      <ChartContainer config={chartConfig} className="w-full max-h-72">
        <LineChart accessibilityLayer data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
          <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
          <YAxis domain={[minScore, maxScore]} tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
          <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
          {seriesKeys.map((key) => {
            const values = chartData.map((row) => row[key]).filter((v): v is number => typeof v === "number");
            const dotted = values.length > 0 && values.every((v) => v < 50);
            return <Line key={key} dataKey={key} type="monotone" stroke={`var(--color-${key})`} strokeWidth={2} strokeDasharray={dotted ? "5 5" : undefined} dot={false} connectNulls />;
          })}
        </LineChart>
      </ChartContainer>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-border">
        {Object.entries(chartConfig).map(([key, { label, color }]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full shrink-0" style={{ backgroundColor: color as string }} />
            <span className="text-xs text-muted-foreground">{label as string}</span>
          </div>
        ))}
      </div>
    </Fragment>
  );
}

function SentimentByChannelSkeleton() {
  return (
    <div className="w-full h-80 flex flex-col justify-between">
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-full rounded" />
        ))}
      </div>
      <div className="relative mt-6 h-full">
        <Skeleton className="absolute bottom-10 left-0 w-full h-1 rounded-full" />
        <Skeleton className="absolute bottom-20 left-0 w-full h-1 rounded-full opacity-60" />
      </div>
    </div>
  );
}
